import { ArrowRight, InfoOutlined } from "@mui/icons-material";
import {
  Button,
  Collapse,
  OutlinedInput,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Menu,
  MenuItem,
  ListItemText,
} from "@mui/material";
import { Box } from "@mui/system";
import React, { ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import {
  Column,
  Filters,
  Row,
  SortingRule,
  useFilters,
  useGlobalFilter,
  usePagination,
  useSortBy,
  useTable,
} from "react-table";
import { PaginatedMetaDto } from "../../service/server/dto/PaginatedDto";
import { Callout } from "../../../polaris/components/Callout";
import { EmptyInfo } from "../../../polaris/components/Empty";
import { DefaultFilter } from "./filters/DefaultFilter";
import { PaginatedFooter } from "./PaginatedFooter";
import clsx from "clsx";
import { UseRowSelectionManager } from "../../hooks/useRowSelectionManager";
import { ApiRouteHelpers } from "../../root/apiRoutes";
import { usePrimaryOrganizationId } from "../../../polaris/hooks";
import axios from "axios";
import {
  DataSubjectRequestSummaryDto,
  OrganizationDataSubjectRequestBatchDto,
} from "../../service/server/dto/DataSubjectRequestDto";
import type { CollectionGroupReferenceDto } from "../../service/server/dto/CollectionGroupDto";
import { RegionGroup } from "../../service/server/dto/RegulationRequestTypeDto";
import { useSnackBar } from "@truevault/aquilo";
import { useRequestCSVDownloader } from "../../hooks/useRequestCSVDownloader";
import {
  DocumentArrowDownIcon,
  MagnifyingGlassIcon,
  RectangleStackIcon,
} from "@heroicons/react/24/outline";
import { Button as AquiloButton, SimpleCheckboxControlled } from "@truevault/aquilo";
import {
  useEditModeReadOnlyGuard,
  useIsEditModeReadOnly,
} from "../../../polaris/edit-mode/useIsEditModeReadOnly";

type SortOption = {
  label: string;
  columnId: string;
  direction: "asc" | "desc";
};

const SORT_OPTIONS: SortOption[] = [
  { label: "Due Date - Descending", columnId: "dueIn", direction: "desc" },
  { label: "Due Date - Ascending", columnId: "dueIn", direction: "asc" },
  { label: "Request Type A → Z", columnId: "type", direction: "asc" },
  { label: "Request Type Z → A", columnId: "type", direction: "desc" },
  { label: "Status A → Z", columnId: "state", direction: "asc" },
  { label: "Status Z → A", columnId: "state", direction: "desc" },
  { label: "Requester A → Z", columnId: "subjectEmailAddress", direction: "asc" },
  { label: "Requester Z → A", columnId: "subjectEmailAddress", direction: "desc" },
];

type ClickableTableRowProps = {
  row: Row<any>;
  canClick?: boolean;
  onRowClick?: (row: Row<any>) => void | Promise<void>;
  canSelect?: boolean;
  isSelected?: boolean;
  onRowSelect?: (row: Row<any>, selected: boolean) => void | Promise<void>;
  hiddenColumnIds?: string[];
};

const ClickableTableRow: React.FC<ClickableTableRowProps> = ({
  row,
  canClick,
  hiddenColumnIds,
  onRowClick = (_row) => {},
}) => {
  const handleClick = useCallback(() => onRowClick(row), [row, onRowClick]);

  return (
    <TableRow
      hover={canClick}
      onClick={canClick ? handleClick : undefined}
      {...row.getRowProps()}
      key={row.id}
    >
      {row.cells
        .filter((cell) => !hiddenColumnIds?.includes(cell.column.id))
        .map((cell, idx) => {
          return (
            <TableCell {...cell.getCellProps()} key={idx}>
              {cell.render("Cell")}
            </TableCell>
          );
        })}
    </TableRow>
  );
};

export type PaginatedTableProps<DataType extends object & { id: string }> = {
  data: DataType[];
  allDataForCurrentFilter?: DataType[];
  columns: Column<any>[];
  pageMeta?: PaginatedMetaDto;
  showFilters?: boolean;
  rowSelectionManager?: UseRowSelectionManager<DataType>;
  filterPanelOpen?: boolean;
  initialPageIndex?: number;
  initialSortBy?: SortingRule<DataType>[];
  initialFilters?: Filters<DataType>;
  initialGlobalFilter?: string;
  onPageChange?: (pageIndex: number) => void | Promise<void>;
  onSortChange?: (sortBy: SortingRule<any>[]) => void | Promise<void>;
  onFiltersChange?: (filters: Filters<any>) => void | Promise<void>;
  onGlobalSearchChange?: (searchTerm: string) => void | Promise<void>;
  onRowClick?: (row: any) => void;
  enableRowClick?: (row: any) => boolean;
  emptyState?: React.ReactNode;
  reloadDataHandler?: () => void /* If something the user does within the table (e.g. create batch) means we should reload data, parent can define how that should happen */;
  batchesTable?: React.ReactNode;
  kind?: "requests" | "basic";
  renderBulkSelectionHeader?: (args: {
    rowSelectionManager: UseRowSelectionManager<DataType>;
    selectedRows: DataType[];
    allDataForCurrentFilter?: DataType[];
    clearSelection: () => void;
    selectAllVisible: () => void;
  }) => ReactNode;
  searchPlaceholder?: string;
  searchInputWidth?: number | string;
  showColumnHeaders?: boolean;
  initialHiddenColumns?: string[];
  visuallyHiddenColumnIds?: string[];
};

type RequestFilter = (request: DataSubjectRequestSummaryDto) => boolean;

type DisabledBatchReason =
  | "NOT_SUPPORTED_REQUEST_TYPE"
  | "NOT_SAME_WORKFLOW"
  | "NOT_SAME_STEP"
  | "NOT_SAME_REGULATION"
  | "NOT_SAME_COLLECTION_GROUP"
  | "NOT_SAME_CONTEXT"
  | "NOT_SELECTED";

interface DisabledBatchResolver {
  actionText: string;
  removeRequestsMatchingFilter: RequestFilter;
  actionAvailableIfTrueForSomeRequest?: RequestFilter;
}

export const PaginatedTable = <DataType extends object & { id: string }>({
  data,
  allDataForCurrentFilter,
  columns,
  pageMeta,
  showFilters = true,
  rowSelectionManager = null,
  filterPanelOpen = true,
  onPageChange = (_pageIndex: number) => {},
  onSortChange,
  onFiltersChange,
  onGlobalSearchChange,
  initialPageIndex = 0,
  initialSortBy = [],
  initialFilters = [],
  initialGlobalFilter = "",
  onRowClick = (_row: any) => {},
  enableRowClick = (_row: any) => false,
  emptyState,
  reloadDataHandler = () => {},
  batchesTable,
  kind = "requests",
  renderBulkSelectionHeader,
  searchPlaceholder,
  searchInputWidth = "250px",
  showColumnHeaders = true,
  initialHiddenColumns = [],
  visuallyHiddenColumnIds = [],
}: PaginatedTableProps<DataType>) => {
  const defaultColumn = useMemo(
    () => ({
      Filter: DefaultFilter,
    }),
    [],
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    gotoPage,
    state,
    setGlobalFilter,
    setSortBy,
  } = useTable(
    {
      columns,
      data,
      defaultColumn,
      manualPagination: true,
      manualSortBy: true,
      manualFilters: true,
      manualGlobalFilter: true,
      autoResetPage: false,
      autoResetSortBy: false,
      autoResetFilters: false,
      autoResetGlobalFilter: false,
      pageCount: pageMeta?.totalPages ?? 0,
      initialState: {
        hiddenColumns: Array.from(new Set(["id", ...(initialHiddenColumns ?? [])])),
        pageIndex: initialPageIndex,
        sortBy: initialSortBy,
        filters: initialFilters,
        globalFilter: initialGlobalFilter,
      },
    },
    useFilters,
    useGlobalFilter,
    useSortBy,
    usePagination,
  );

  const { pageIndex, pageSize, sortBy, filters, globalFilter } = state;

  const globalSearchPlaceholder = searchPlaceholder ?? "Email / Request ID";
  const enableBatchFeatures = Boolean(rowSelectionManager) && kind === "requests";
  const selectedRows = Object.values(rowSelectionManager?.selectedRowsHash ?? {}) as DataType[];

  const updatePageIndex = useCallback(
    (newPageIndex) => {
      onPageChange(newPageIndex + 1);
      gotoPage(newPageIndex);
    },
    [gotoPage, onPageChange],
  );

  useEffect(() => {
    if (onSortChange) {
      onSortChange(sortBy);
    }
  }, [sortBy, onSortChange]);

  useEffect(() => {
    if (onFiltersChange) {
      onFiltersChange(filters);
    }
  }, [filters, onFiltersChange, updatePageIndex]);

  useEffect(() => {
    if (onGlobalSearchChange) {
      onGlobalSearchChange(globalFilter);
    }
  }, [globalFilter, onGlobalSearchChange, updatePageIndex]);

  // <-- FUTURE: Would be nice to extract this logic somewhere else instead of the generic PaginatedTable component, to avoid the new reference to DataSubjectRequestSummaryDto from this generic-ish component.
  const orgId = usePrimaryOrganizationId();
  const csvDownloader = useRequestCSVDownloader({ orgId });

  // Helper functions to get group identifiers for comparison
  const getGroupIds = (groups?: CollectionGroupReferenceDto[] | null): string[] => {
    if (!groups?.length) return [];
    return groups
      .map((group) => group?.id)
      .filter((id): id is string => Boolean(id))
      .sort();
  };

  const getGroupNames = (groups?: CollectionGroupReferenceDto[] | null): string[] => {
    if (!groups?.length) return [];
    return groups
      .map((group) => group?.name)
      .filter((name): name is string => Boolean(name))
      .sort();
  };

  const selectedRowsHash = rowSelectionManager?.selectedRowsHash;
  const selectedRequests = useMemo(() => {
    if (!selectedRowsHash) {
      return [];
    }

    return Object.values(selectedRowsHash).map(
      (request) => request as unknown as DataSubjectRequestSummaryDto,
    );
  }, [selectedRowsHash]);

  /**
   * We only support batches of requests that are all the same general workflow (e.g., Opt-Out) and in the same step.
   * Also for now, only the opt-out workflow is supported since each batch type requires workflow-specific downstream
   * changes.
   */
  const createBatchDisabledReason = useMemo((): DisabledBatchReason | null => {
    if (!enableBatchFeatures || !rowSelectionManager) {
      return null;
    }

    if (!rowSelectionManager || selectedRequests.length < 2) {
      // Doesn't make sense to create a batch of 0 or 1 requests.
      return "NOT_SELECTED";
    }

    const [referenceRequest, ...rest] = selectedRequests;
    const referenceWorkflow = referenceRequest.dataRequestType.workflowType;
    const referenceStep = referenceRequest.state;
    const referenceContext = referenceRequest.context;

    if (referenceWorkflow !== "OPT_OUT" && referenceWorkflow !== "LIMIT") {
      return "NOT_SUPPORTED_REQUEST_TYPE";
    }

    if (rest.some((request) => request.dataRequestType.workflowType !== referenceWorkflow)) {
      return "NOT_SAME_WORKFLOW";
    }

    if (rest.some((request) => request.state !== referenceStep)) {
      return "NOT_SAME_STEP";
    }

    // Check if all selected requests have the same context (HR vs Consumer)
    if (rest.some((request) => request.context !== referenceContext)) {
      return "NOT_SAME_CONTEXT";
    }

    // OTHER (e.g., Voluntary requests from US states without a privacy law) is treated as
    // UNITED_STATES here, mirroring downstream batch processing: the vendor list is computed ONE
    // time from the representative request and applied to every batch member
    // (RemoveConsumer.tsx / DataSubjectRequestService.contactVendors), and OTHER maps to the
    // UNITED_STATES processing region on both ends (getProcessingRegionFromRegionGroup in
    // vendors.ts, RequestAutomationService.getProcessingRegionForRequest). So OTHER and US members
    // always resolve to the same vendor list, while OTHER mixed with Canada or EEA/UK (e.g., a
    // PIPEDA opt-out) would apply the representative's vendor list to members whose own region maps
    // to a different one — that stays blocked, as does mixing the primary regions with each other.
    const selectedRegionGroups = new Set<RegionGroup>(
      selectedRequests
        .map((request) => request.dataRequestType.regulationLaw?.region?.regionGroup)
        .filter((group): group is RegionGroup => Boolean(group))
        .map((group) => (group === RegionGroup.OTHER ? RegionGroup.UNITED_STATES : group)),
    );

    if (selectedRegionGroups.size > 1) {
      return "NOT_SAME_REGULATION";
    }

    if (selectedRequests.length >= 2) {
      // Compare all requests against the first one
      const referenceGroupIds = getGroupIds(referenceRequest.consumerGroups);
      const hasCollectionGroupMismatch = rest.some((request) => {
        const currentGroupIds = getGroupIds(request.consumerGroups);
        return (
          currentGroupIds.length !== referenceGroupIds.length ||
          currentGroupIds.some((id, index) => id !== referenceGroupIds[index])
        );
      });

      if (hasCollectionGroupMismatch) {
        return "NOT_SAME_COLLECTION_GROUP";
      }
    }

    return null;
  }, [enableBatchFeatures, rowSelectionManager, selectedRequests]);

  let createBatchDisabledMessage = "";
  switch (createBatchDisabledReason) {
    case "NOT_SUPPORTED_REQUEST_TYPE":
      createBatchDisabledMessage = "Currently only Opt-Out and Limit requests can be batched.";
      break;
    case "NOT_SAME_WORKFLOW":
      createBatchDisabledMessage = "Requests must all share the same workflow type to be batched.";
      break;
    case "NOT_SAME_STEP":
      createBatchDisabledMessage = "Requests must all be at the same step to be batched.";
      break;
    case "NOT_SAME_REGULATION":
      createBatchDisabledMessage =
        "Requests must all be from the same regulatory region to be batched. Other-region requests can be batched with US requests.";
      break;
    case "NOT_SAME_COLLECTION_GROUP":
      createBatchDisabledMessage =
        "Requests must have the same collection groups to be batched together.";
      break;
    case "NOT_SAME_CONTEXT":
      createBatchDisabledMessage = "HR requests and consumer requests cannot be batched together.";
      break;
    case "NOT_SELECTED":
      createBatchDisabledMessage = "At least two requests must to be selected to create a batch.";
      break;
  }

  /**
   * Given a filter, run it against all selected requests and remove from the selection any that match the filter.
   */
  const applyRequestFilter = useCallback(
    (filter: RequestFilter) => {
      if (!enableBatchFeatures || !rowSelectionManager) {
        return;
      }

      const removeHashes = [];
      Object.values(rowSelectionManager.selectedRowsHash).forEach((request) => {
        const r = request as unknown as DataSubjectRequestSummaryDto;
        if (filter(r)) {
          removeHashes.push(r.id);
        }
      });
      removeHashes.forEach((hash) => rowSelectionManager.deselectRow(hash));
    },
    [enableBatchFeatures, rowSelectionManager],
  );

  const definedResolversForDisabledOptOut = useMemo((): DisabledBatchResolver[] => {
    if (!enableBatchFeatures || !rowSelectionManager) {
      return [];
    }

    switch (createBatchDisabledReason) {
      case "NOT_SUPPORTED_REQUEST_TYPE":
      case "NOT_SAME_WORKFLOW":
        // Allow filtering down to only opt-outs or limits.
        return [
          {
            actionText: "Select only Limits",
            removeRequestsMatchingFilter: (r) => r.dataRequestType.workflowType !== "LIMIT",
            actionAvailableIfTrueForSomeRequest: (r) => r.dataRequestType.workflowType === "LIMIT",
          },
          {
            actionText: "Select only Opt-Outs",
            removeRequestsMatchingFilter: (r) => r.dataRequestType.workflowType !== "OPT_OUT",
            actionAvailableIfTrueForSomeRequest: (r) =>
              r.dataRequestType.workflowType === "OPT_OUT",
          },
        ];
      case "NOT_SAME_STEP":
        // Mix of different steps - selectively show resolvers based on the mix of selections.
        return [
          {
            actionText: "Select only Remove Consumer",
            removeRequestsMatchingFilter: (r) => r.state !== "REMOVE_CONSUMER",
            actionAvailableIfTrueForSomeRequest: (r) => r.state === "REMOVE_CONSUMER",
          },
          {
            actionText: "Select only Contact Vendors",
            removeRequestsMatchingFilter: (r) => r.state !== "CONTACT_VENDORS",
            actionAvailableIfTrueForSomeRequest: (r) => r.state === "CONTACT_VENDORS",
          },
          {
            actionText: "Select only Limit Data",
            removeRequestsMatchingFilter: (r) => r.state !== "LIMIT_DATA",
            actionAvailableIfTrueForSomeRequest: (r) => r.state === "LIMIT_DATA",
          },
          {
            actionText: "Select only Notify Consumer",
            removeRequestsMatchingFilter: (r) => r.state !== "NOTIFY_CONSUMER",
            actionAvailableIfTrueForSomeRequest: (r) => r.state === "NOTIFY_CONSUMER",
          },
        ];
      case "NOT_SAME_REGULATION": {
        // Provide context-aware resolution options based on the selected regions. Each option
        // narrows the selection to a single region; the US option keeps OTHER (e.g., Voluntary)
        // requests since those can be batched with US.
        const hasOtherSelected = selectedRequests.some(
          (r) => r.dataRequestType.regulationLaw?.region?.regionGroup === RegionGroup.OTHER,
        );
        const makeRegionResolver = (
          label: string,
          regionGroups: RegionGroup[],
        ): DisabledBatchResolver => ({
          actionText: `Select only ${label}`,
          removeRequestsMatchingFilter: (r) => {
            const group = r.dataRequestType.regulationLaw?.region?.regionGroup;
            return !group || !regionGroups.includes(group);
          },
          actionAvailableIfTrueForSomeRequest: (r) => {
            const group = r.dataRequestType.regulationLaw?.region?.regionGroup;
            return Boolean(group && regionGroups.includes(group));
          },
        });
        return [
          makeRegionResolver(hasOtherSelected ? "US & Other" : "US", [
            RegionGroup.UNITED_STATES,
            RegionGroup.OTHER,
          ]),
          makeRegionResolver("Canada", [RegionGroup.CANADA]),
          makeRegionResolver("EEA/UK", [RegionGroup.EEA_UK]),
        ];
      }
      case "NOT_SAME_COLLECTION_GROUP": {
        // Get unique collection group ID combinations
        const uniqueGroupCombinations = new Map<string, { ids: string[]; names: string[] }>();

        selectedRequests.forEach((request) => {
          if (request.consumerGroups && request.consumerGroups.length > 0) {
            const sortedIds = getGroupIds(request.consumerGroups);
            const sortedNames = getGroupNames(request.consumerGroups);
            const combinationKey = sortedIds.join("|");
            uniqueGroupCombinations.set(combinationKey, { ids: sortedIds, names: sortedNames });
          }
        });

        return Array.from(uniqueGroupCombinations.entries()).map(([combinationKey, { names }]) => {
          const formatActionText = (names: string[]): string => {
            if (names.length === 1) {
              return `Select only ${names[0]} requests`;
            }
            if (names.length === 2) {
              return `Select only ${names.join(" and ")} requests`;
            }
            return `Select only ${names.slice(0, -1).join(", ")} and ${
              names[names.length - 1]
            } requests`;
          };

          const getRequestCombinationKey = (
            request: DataSubjectRequestSummaryDto,
          ): string | null => {
            if (!request.consumerGroups?.length) return null;
            return getGroupIds(request.consumerGroups).join("|");
          };

          return {
            actionText: formatActionText(names),
            removeRequestsMatchingFilter: (r) => {
              const requestKey = getRequestCombinationKey(r);
              return !requestKey || requestKey !== combinationKey;
            },
            actionAvailableIfTrueForSomeRequest: (r) => {
              const requestKey = getRequestCombinationKey(r);
              return requestKey === combinationKey;
            },
          };
        });
      }
      case "NOT_SAME_CONTEXT":
        // Mix of HR and Consumer requests - allow selecting one or the other.
        return [
          {
            actionText: "Select only Consumer requests",
            removeRequestsMatchingFilter: (r) => r.context !== "CONSUMER",
            actionAvailableIfTrueForSomeRequest: (r) => r.context === "CONSUMER",
          },
          {
            actionText: "Select only HR requests",
            removeRequestsMatchingFilter: (r) => r.context !== "EMPLOYEE",
            actionAvailableIfTrueForSomeRequest: (r) => r.context === "EMPLOYEE",
          },
        ];
      case "NOT_SELECTED":
        // Nothing is selected, no automatic resolutions possible.
        break;
    }
    return [];
  }, [enableBatchFeatures, createBatchDisabledReason, rowSelectionManager, selectedRequests]);

  /**
   * Filter down the definitions of possible resolvers to only the ones that we should show, based on which requests are
   * currently selected.
   */
  const applicableResolversForDisabledOptOut = useMemo(() => {
    if (!enableBatchFeatures || !rowSelectionManager) {
      return [];
    }

    return definedResolversForDisabledOptOut?.filter(
      (resolver) =>
        !resolver.actionAvailableIfTrueForSomeRequest ||
        selectedRequests.some(resolver.actionAvailableIfTrueForSomeRequest),
    );
  }, [
    enableBatchFeatures,
    definedResolversForDisabledOptOut,
    rowSelectionManager,
    selectedRequests,
  ]);

  const snackbar = useSnackBar();
  const [isCreatingBatch, setIsCreatingBatch] = useState<boolean>(false);
  const isEditModeReadOnly = useIsEditModeReadOnly();
  const guardReadOnlyMode = useEditModeReadOnlyGuard();
  const handleCreateBatch = async () => {
    if (!enableBatchFeatures || !rowSelectionManager) {
      return;
    }
    if (guardReadOnlyMode()) {
      return;
    }

    setIsCreatingBatch(true);
    const response = await axios.post<{
      success: boolean;
      batch: OrganizationDataSubjectRequestBatchDto;
    }>(ApiRouteHelpers.organizationRequestBatchCreate(orgId), {
      requestList: Object.keys(rowSelectionManager.selectedRowsHash),
    });

    if (response?.data?.success && response?.data?.batch) {
      snackbar(`Batch created from ${response.data.batch.requestCount} requests`, {
        variant: "success",
        body: "Process it or download its CSV from the Batches table.",
      });
    } else {
      snackbar(`Couldn't create batch, try again later`, { variant: "error" });
    }
    rowSelectionManager.clearSelectedRows();
    if (reloadDataHandler) {
      reloadDataHandler();
    }
    setIsCreatingBatch(false);
  };

  const countBatchedRequests = useMemo(() => {
    if (!enableBatchFeatures) {
      return 0;
    }
    return allDataForCurrentFilter?.filter((r) => !!(r as any).batchPublicId).length ?? 0;
  }, [enableBatchFeatures, allDataForCurrentFilter]);
  const countSelectableRequests = enableBatchFeatures
    ? (allDataForCurrentFilter?.length ?? 0) - countBatchedRequests
    : 0;

  // (end of future refactor for batch processing) -->

  const showBulkSelectionHeader =
    rowSelectionManager?.isSelecting && rowSelectionManager?.hasVisibleRows;

  const bulkSelectionHeaderContent = (() => {
    if (!showBulkSelectionHeader || !rowSelectionManager) {
      return null;
    }

    if (renderBulkSelectionHeader) {
      return renderBulkSelectionHeader({
        rowSelectionManager,
        selectedRows,
        allDataForCurrentFilter,
        clearSelection: () => rowSelectionManager.setSelectedRowsHash({}),
        selectAllVisible: () => rowSelectionManager.setSelectionForVisible(true),
      });
    }

    if (!enableBatchFeatures) {
      return null;
    }

    const createBatchDisabledTooltipContent =
      createBatchDisabledReason || applicableResolversForDisabledOptOut?.length ? (
        <>
          <span>{createBatchDisabledMessage}</span>
          {applicableResolversForDisabledOptOut?.length ? (
            <>
              <p
                style={{ fontWeight: "bold", fontVariant: "small-caps" }}
                className="text-color-primary-light mb-xs"
              >
                Quick Fixes
              </p>
              <div>
                {applicableResolversForDisabledOptOut.map((resolver) => {
                  return (
                    <Button
                      key={resolver.actionText}
                      onClick={() => {
                        applyRequestFilter(resolver.removeRequestsMatchingFilter);
                      }}
                      style={{
                        color: "#f3f0fe",
                        width: "100%;",
                        whiteSpace: "normal",
                      }}
                    >
                      <ArrowRight />
                      {resolver.actionText}
                    </Button>
                  );
                })}
              </div>
            </>
          ) : null}
        </>
      ) : null;

    return (
      <BulkSelectedRowsHeader
        rowSelectionManager={rowSelectionManager}
        countSelectableRequests={countSelectableRequests}
        onClearSelectionClick={() => {
          rowSelectionManager.setSelectedRowsHash({});
        }}
        onSelectAllClick={() => {
          if (allDataForCurrentFilter) {
            rowSelectionManager.selectAllRows(allDataForCurrentFilter);
          }
        }}
        onCsvDownloadClick={() => {
          void csvDownloader.downloadRequests(Object.keys(rowSelectionManager.selectedRowsHash));
        }}
        createBatchDisabled={!!createBatchDisabledReason || isCreatingBatch}
        isEditModeReadOnly={isEditModeReadOnly}
        createBatchDisabledTooltip={createBatchDisabledTooltipContent}
        onCreateBatchClick={handleCreateBatch}
        currentSort={sortBy?.[0] || { id: "dueIn", desc: true }}
        onSortChange={(columnId: string, direction: "asc" | "desc") => {
          const newSort = [{ id: columnId, desc: direction === "desc" }];
          setSortBy(newSort);
          if (onSortChange) {
            onSortChange(newSort);
          }
        }}
      />
    );
  })();

  return (
    <>
      {showFilters && (
        <Collapse in={filterPanelOpen}>
          <Box sx={{ my: 1 }} data-edit-mode-read-only-allow>
            <Callout>
              {headerGroups.map((headerGroup, index) => (
                <div
                  className="flex-row flex--expand flex--between"
                  {...headerGroup.getHeaderGroupProps()}
                  key={`hg-${index}`}
                >
                  <div>
                    <OutlinedInput
                      onChange={(e) => {
                        setGlobalFilter(e.target.value);
                      }}
                      type="search"
                      size="small"
                      sx={{ background: "white", width: searchInputWidth }}
                      startAdornment={
                        <MagnifyingGlassIcon
                          style={{
                            height: "16px",
                            width: "16px",
                            marginRight: "10px",
                            color: "#726D83",
                          }}
                        />
                      }
                      value={globalFilter}
                      placeholder={globalSearchPlaceholder}
                    />
                  </div>
                  {headerGroup.headers
                    .filter((c) => c.canFilter)
                    .sort((a, b) => {
                      // @ts-ignore custom properties added for filter columns
                      return (a?.filterDisplayOrder || 0) - (b?.filterDisplayOrder || 0);
                    })
                    .map((column) => (
                      <div {...column.getHeaderProps()} key={column.id}>
                        {column.render("Filter")}
                      </div>
                    ))}
                </div>
              ))}
            </Callout>
          </Box>
        </Collapse>
      )}

      {enableBatchFeatures && batchesTable && (
        <div style={{ marginBottom: "24px" }}>{batchesTable}</div>
      )}

      <div className={clsx("pb-lg", rowSelectionManager?.isSelecting && "mt-md")}>
        {/* FUTURE: this part of the component should be modularized if we add support for bulk selection beyond Request objects, since the record actions & verbiage here are Request-specific */}
        {bulkSelectionHeaderContent}
        <TableContainer className="mb-lg">
          <Table
            className={"request-table"}
            {...getTableProps()}
            sx={showBulkSelectionHeader ? { marginTop: "0 !important" } : undefined}
          >
            {!showBulkSelectionHeader && showColumnHeaders && (
              <TableHead>
                {headerGroups.map((headerGroup, index) => (
                  <TableRow {...headerGroup.getHeaderGroupProps()} key={`hg-${index}`}>
                    {headerGroup.headers.map((column) => {
                      if (visuallyHiddenColumnIds?.includes(column.id)) {
                        return null;
                      }

                      return (
                        <TableCell
                          component="th"
                          {...column.getHeaderProps(
                            column.getSortByToggleProps({
                              title:
                                column.id === "select" ? "Select All" : "Toggle Sort Direction",
                            }),
                          )}
                          key={column.id}
                        >
                          {column.render("Header")}

                          {column.canSort && (
                            <TableSortLabel
                              active={column.isSorted}
                              direction={column.isSortedDesc ? "desc" : "asc"}
                            />
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </TableHead>
            )}

            <TableBody {...getTableBodyProps()}>
              {page.length > 0 ? (
                <>
                  {page.map((row) => {
                    prepareRow(row);
                    return (
                      <ClickableTableRow
                        key={row.id}
                        row={row}
                        canClick={enableRowClick(row)}
                        hiddenColumnIds={visuallyHiddenColumnIds}
                        onRowClick={(r: Row) => {
                          if (rowSelectionManager?.isSelecting) {
                            const rowData = r?.original as DataType;
                            if (!rowData) {
                              return;
                            }
                            rowSelectionManager?.onRowSelectChange(
                              rowData.id,
                              !rowSelectionManager?.isRowSelected(rowData.id),
                              rowData,
                            );
                          } else {
                            onRowClick(r);
                          }
                        }}
                      />
                    );
                  })}
                </>
              ) : (
                <TableRow>
                  <TableCell colSpan={10000000}>
                    {emptyState ? (
                      emptyState
                    ) : (
                      <EmptyInfo Icon={InfoOutlined}>No Requests</EmptyInfo>
                    )}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {pageMeta && pageMeta.totalPages > 1 && (
          <PaginatedFooter
            meta={{
              page: pageIndex,
              per: pageSize,
              count: page.length,
              totalPages: pageMeta.totalPages,
              totalCount: pageMeta.totalCount,
            }}
            setPage={updatePageIndex}
          />
        )}
      </div>
    </>
  );
};

function BulkSelectedRowsHeader({
  rowSelectionManager,
  countSelectableRequests,
  onClearSelectionClick,
  onSelectAllClick,
  onCsvDownloadClick,
  createBatchDisabled,
  createBatchDisabledTooltip,
  onCreateBatchClick,
  currentSort,
  onSortChange,
  isEditModeReadOnly,
}: {
  rowSelectionManager: UseRowSelectionManager<any>;
  countSelectableRequests: number;
  onClearSelectionClick: () => void;
  onSelectAllClick: () => void;
  onCsvDownloadClick: () => void;
  createBatchDisabled: boolean;
  createBatchDisabledTooltip: React.ReactNode;
  onCreateBatchClick: () => void;
  currentSort: SortingRule<any>;
  onSortChange: (columnId: string, direction: "asc" | "desc") => void;
  isEditModeReadOnly?: boolean;
}) {
  const [sortMenuAnchor, setSortMenuAnchor] = useState<null | HTMLElement>(null);
  const sortButtonRef = React.useRef<HTMLDivElement>(null);
  const sortMenuOpen = Boolean(sortMenuAnchor);

  const getCurrentSortLabel = () => {
    const option = SORT_OPTIONS.find(
      (opt) => opt.columnId === currentSort.id && (opt.direction === "desc") === currentSort.desc,
    );
    return option?.label || "Due Date - Descending";
  };

  const handleSortMenuClick = () => {
    setSortMenuAnchor(sortButtonRef.current);
  };

  const handleSortMenuClose = () => {
    setSortMenuAnchor(null);
  };

  const handleSortSelect = (option: SortOption) => {
    onSortChange(option.columnId, option.direction);
    handleSortMenuClose();
  };

  const selectedRequestsCount = rowSelectionManager?.selectedRowCount ?? 0;
  const groupButtonLabel = `Group for Bulk Processing (${selectedRequestsCount})`;

  let createBatchButton = (
    <AquiloButton
      size="s"
      color="primary"
      onClick={onCreateBatchClick}
      disabled={isEditModeReadOnly || createBatchDisabled}
    >
      <RectangleStackIcon className="w-[12px] h-[16px] mr-[8px]" /> {groupButtonLabel}
    </AquiloButton>
  );

  return (
    <div className="tw-boundary">
      <div className="flex justify-between items-center pl-[8px] pb-[8px] border-b border-neutral-300">
        <div className="flex gap-[12px] align-center">
          <div className="self-center">
            <SimpleCheckboxControlled
              name={undefined}
              checked={rowSelectionManager?.areAllVisibleRowsSelected()}
              onChange={(newChecked) => {
                rowSelectionManager?.setSelectionForVisible(newChecked);
              }}
            />
          </div>
          <div>
            {createBatchDisabled && createBatchDisabledTooltip ? (
              <Tooltip title={createBatchDisabledTooltip}>
                {/* Wrapper <div> is necessary for the Tooltip to work */}
                <div>{createBatchButton}</div>
              </Tooltip>
            ) : (
              createBatchButton
            )}
          </div>
        </div>
        <div className="flex gap-[12px]">
          <AquiloButton size="s" color="tertiary" onClick={onClearSelectionClick}>
            Clear Selection
          </AquiloButton>
          <AquiloButton size="s" color="tertiary" onClick={onSelectAllClick}>
            {`Select all ${countSelectableRequests} Requests`}
          </AquiloButton>
        </div>
        <div className="flex gap-[12px]">
          <div ref={sortButtonRef}>
            <AquiloButton
              size="s"
              color="tertiary"
              onClick={handleSortMenuClick}
              aria-controls={sortMenuOpen ? "sort-menu" : undefined}
              aria-haspopup="true"
              aria-expanded={sortMenuOpen ? "true" : undefined}
            >
              Sort By: {getCurrentSortLabel()}
            </AquiloButton>
            <Menu
              id="sort-menu"
              anchorEl={sortMenuAnchor}
              open={sortMenuOpen}
              onClose={handleSortMenuClose}
              MenuListProps={{
                "aria-labelledby": "sort-button",
              }}
            >
              {SORT_OPTIONS.map((option) => (
                <MenuItem
                  key={`${option.columnId}-${option.direction}`}
                  onClick={() => handleSortSelect(option)}
                  selected={
                    option.columnId === currentSort.id &&
                    (option.direction === "desc") === currentSort.desc
                  }
                >
                  <ListItemText primary={option.label} />
                </MenuItem>
              ))}
            </Menu>
          </div>
          <div>
            <AquiloButton size="s" color="tertiary" onClick={onCsvDownloadClick}>
              <DocumentArrowDownIcon className="w-[12px] h-[16px] mr-[8px]" /> Download CSV
            </AquiloButton>
          </div>
        </div>
      </div>
    </div>
  );
}
